import withResponsive from '../hoc/withResponsive'
import translate from '../utils/translations'
import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'

export default withResponsive({
  name: 'occasions-list',

  componentDidMount () {
    this._currentPage = 1
    this._perPage     = parseInt(this.el.dataset.perPage, 10) || 10
    this._total       = parseInt(this.el.dataset.total, 10) || 0
    this._shown       = Math.min(this._perPage, this._total)
    this._lastFilters = {}
    this._currentSort = 'newest'

    // Initialiser la barre de progression au chargement
    this._updateProgress()

    // Filtres — remplace la grille et repart de la page 1
    document.addEventListener('occasions:filter-change', (e) => {
      this._currentPage = 1
      this._shown       = Math.min(this._perPage, this._total)
      this._onFilterChange(e.detail)
    })

    // Changement de tri
    document.addEventListener('occasions:sort-change', (e) => {
      this._currentSort = e.detail.value
      this._currentPage = 1
      this._shown       = Math.min(this._perPage, this._total)
      this._onFilterChange(this._lastFilters)
    })

    // Bouton "Load more" — délégation d'événement
    this.el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-occasions-load-more]')
      if (!btn) return
      this._currentPage++
      this._loadMore()
    })
  },

  // ─── Handler filtre (remplace la grille, retour page 1) ────────────────

  _onFilterChange (filterState) {
    this._lastFilters = filterState || {}

    const filters  = this._buildFilters(this._lastFilters)
    filters.page   = 1

    this._setLoading(true)

    this.fetchOccasionsHTML(filters)
      .then(result => {
        if (!result) return
        if (result.html !== null) this._updateGrid(result.html)
        if (result.total !== null) {
          this._total = result.total
          this._shown = Math.min(this._perPage, this._total)
        }
        this._showLoadMoreBtn()
        this._updateProgress()
        this._scrollToGridIfBelow()
      })
      .finally(() => {
        this._setLoading(false)
      })
  },

  // ─── Load more (ajoute les items suivants à la grille) ─────────────────

  _loadMore () {
    const filters = this._buildFilters(this._lastFilters)
    filters.page  = this._currentPage

    this._setLoading(true)

    this.fetchOccasionsHTML(filters)
      .then(result => {
        if (!result || !result.html) return
        this._appendToGrid(result.html)
        this._shown = Math.min(this._currentPage * this._perPage, this._total)
        this._updateProgress()
      })
      .finally(() => {
        this._setLoading(false)
      })
  },

  // ─── Construction des filtres AJAX ─────────────────────────────────────

  _buildFilters (filterState = {}) {
    const {
      model, location, warrantyPeriod, fuelType, category,
      price_min, price_max,
      loa_min, loa_max,
      year_min, year_max,
      motor_min, motor_max,
    } = filterState

    const SORT_MAP = {
      newest:     { order_col: 'creation_date', order_dir: 'DESC' },
      oldest:     { order_col: 'creation_date', order_dir: 'ASC'  },
      price_asc:  { order_col: 'price',         order_dir: 'ASC'  },
      price_desc: { order_col: 'price',         order_dir: 'DESC' },
    }
    const sort = SORT_MAP[this._currentSort] || SORT_MAP.newest

    const dufour  = this.el.dataset.dufour ?? 'false'
    const filters = { dufour, ...sort }

    if (category && category.length > 0) {
      filters.category = category
    }

    if (model && model.length > 0)                   filters.model_name         = model
    if (location && location.length > 0)             filters.location           = location
    if (warrantyPeriod && warrantyPeriod.length > 0) filters.warranty_period    = warrantyPeriod

    // Motorisation : valeurs discrètes de type de carburant cochées → fuel_type IN (...)
    if (fuelType && fuelType.length > 0)              filters.fuel_type          = fuelType
    if (price_min)   filters.price_min         = price_min
    if (price_max)   filters.price_max         = price_max
    if (loa_min)     filters.length_metric_min = loa_min
    if (loa_max)     filters.length_metric_max = loa_max
    if (year_min)    filters.year_min          = year_min
    if (year_max)    filters.year_max          = year_max
    if (motor_min)   filters.power_min         = motor_min
    if (motor_max)   filters.power_max         = motor_max

    return filters
  },

  // ─── Fetch HTML rendu par Timber ────────────────────────────────────────

  fetchOccasionsHTML (filters = {}) {
    const ajaxUrl = window.ajaxurl && window.ajaxurl[0]
      ? window.ajaxurl[0]
      : '/wp-admin/admin-ajax.php'

    const formData = new FormData()
    formData.append('action', 'fp_occasions_filter')

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => formData.append(key + '[]', v))
      } else {
        formData.append(key, value)
      }
    })

    return fetch(ajaxUrl, { method: 'POST', body: formData })
      .then(res => res.json())
      .then(data => {
        if (!data.success) return null
        return {
          html:        data.data?.html        ?? null,
          total:       data.data?.total       ?? null,
          total_pages: data.data?.total_pages ?? null,
        }
      })
      .catch(err => {
        console.error('[occasions-list]', err)
        return null
      })
  },

  // ─── Fetch JSON brut (usage futur / autres composants) ──────────────────

  fetchOccasions (filters = {}) {
    const ajaxUrl = window.ajaxurl && window.ajaxurl[0]
      ? window.ajaxurl[0]
      : '/wp-admin/admin-ajax.php'

    const formData = new FormData()
    formData.append('action', 'wp_fp_zoho_boarding_pass__wp_fp_zoho__get_velite_boats')

    Object.entries(filters).forEach(([key, value]) => {
      formData.append(key, value)
    })

    return fetch(ajaxUrl, { method: 'POST', body: formData })
      .then(res => res.json())
      .then(data => {
        if (!data.success || !Array.isArray(data.data)) return []
        return data.data
      })
      .catch(err => {
        console.error('[occasions-list]', err)
        return []
      })
  },

  // ─── DOM helpers ────────────────────────────────────────────────────────

  _updateGrid (html) {
    const wrapper = this.el.querySelector('[data-occasions-grid]')
    if (!wrapper) return
    wrapper.innerHTML = html
  },

  // ─── Scroll to results top if user is scrolled past it ─────────────────

  _scrollToGridIfBelow () {
    // Use the toolbar as the scroll target so the user lands just above the grid
    const target = this.el.querySelector('.occasions-list__toolbar') || this.el
    const targetTop = target.getBoundingClientRect().top + window.scrollY

    if (window.scrollY > targetTop) {
      window.scrollTo({ top: targetTop, behavior: 'smooth' })
    }
  },


  _appendToGrid (html) {
    const wrapper = this.el.querySelector('[data-occasions-grid]')
    if (!wrapper) return
    const ul = wrapper.querySelector('.occasions-list__grid')
    if (!ul) return
    const temp   = document.createElement('div')
    temp.innerHTML = html
    const newUl  = temp.querySelector('.occasions-list__grid')
    if (!newUl) return

    ul.insertAdjacentHTML('beforeend', newUl.innerHTML)
    this._initNewSliders(ul)
  },

  // Initialise Swiper sur les nouveaux [js-slider-el] qui ne l'ont pas encore
  _initNewSliders (container) {
    container.querySelectorAll('[js-slider-el]').forEach(swiperEl => {
      if (swiperEl.swiper) return // déjà initialisé

      const sliderRoot = swiperEl.closest('[js-slider]')
      const navEl      = sliderRoot
        ? [...sliderRoot.querySelectorAll('[js-slider-navigation]')].find(n => !swiperEl.contains(n))
        : null
      const currentEl  = sliderRoot ? sliderRoot.querySelector('[js-slider-current-slide]') : null

      const swiper = new Swiper(swiperEl, {
        modules: [Navigation],
        slidesPerView: 1,
        spaceBetween: 0,
        threshold: 30,
        ...(navEl ? {
          navigation: {
            nextEl: navEl.querySelector('.swiper-button-next'),
            prevEl: navEl.querySelector('.swiper-button-prev'),
          },
        } : {}),
      })

      if (currentEl) {
        swiper.on('slideChangeTransitionEnd', () => {
          const i = swiper.realIndex + 1
          currentEl.innerHTML = i < 10 ? `0${i}` : String(i)
        })
      }
    })
  },

  _updateProgress () {
    const textEl  = this.el.querySelector('[data-occasions-pagination-text]')
    const barEl   = this.el.querySelector('[data-occasions-pagination-bar]')
    const btn     = this.el.querySelector('[data-occasions-load-more]')
    const countEl = document.querySelector('[data-occasions-count]')

    if (textEl) {
      textEl.textContent = translate('OCCASIONS_PROGRESS')
        .replace('*current*', this._shown)
        .replace('*total*', this._total)
    }

    if (barEl && this._total > 0) {
      barEl.style.width = `${Math.round(this._shown / this._total * 100)}%`
    }

    if (btn) {
      btn.classList.toggle('is-hidden', this._shown >= this._total)
    }

    // Mettre à jour le compteur dans occasions-filters-recap
    if (countEl) {
      countEl.textContent = this._shown
    }
  },

  _showLoadMoreBtn () {
    const btn = this.el.querySelector('[data-occasions-load-more]')
    if (btn) btn.classList.remove('is-hidden')
  },

  _setLoading (isLoading) {
    this.el.setAttribute('aria-busy', isLoading ? 'true' : 'false')
    this.el.classList.toggle('is-loading', isLoading)
  },
})
